import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TechnicianService } from '../../../services/technician.service';
import { WorkOrdersOverviewService } from '../../../services/work-orders-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { WorkOrder, WorkOrderItem, ScheduleWorkOrderRequest, ScheduleAndAssignFormData } from '../../../models/work-orders-overview.models';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { WorkOrderTechnician } from '../../../models/technician.model';
import { AvatarComponent } from '../../../../../../_shared/components/avatar/avatar.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-schedule-and-assign-modal',
  standalone: true,
  imports: [NgbModule, CommonModule, FormsModule, FlatpickrDatePickerComponent, AvatarComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './schedule-and-assign-modal.component.html',
  styleUrl: './schedule-and-assign-modal.component.scss'
})
export class ScheduleAndAssignModalComponent implements OnInit, OnDestroy {
  @Input() workOrderData: WorkOrder | WorkOrderItem | null = null;
  @Input() workOrderId: number | null = null;
  @Input() approveMode: boolean = false;
  @Input() onApprove: ((scheduledDate: Date, technicianIds: number[]) => Promise<void>) | null = null;

  private technicianService = inject(TechnicianService);
  private workOrdersService = inject(WorkOrdersOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancelSubject = new Subject<void>();

  // Step management
  currentStep: number = 1;
  totalSteps: number = 2;

  // Form data
  formData: ScheduleAndAssignFormData = {
    scheduledDate: '',
    scheduledTime: '',
    technicianIds: []
  };

  // Technician data
  technicians: WorkOrderTechnician[] = [];
  filteredTechnicians: WorkOrderTechnician[] = [];
  searchQuery: string = '';

  // State
  isLoading: boolean = false;
  isLoadingTechnicians: boolean = false;
  timeDropdownOpen: boolean = false;

  // Time options
  timeOptions: string[] = [];

  // Field touched tracking
  touched = {
    scheduledDate: false,
    scheduledTime: false
  };

  constructor(private modalInstance: NgbActiveModal) {
    this.generateTimeOptions();
  }

  ngOnInit(): void {
    this.loadTechnicians();
    this.initializeFormData();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private initializeFormData(): void {
    if (this.workOrderData) {
      // Set scheduled date if available
      if (this.workOrderData.scheduledDate) {
        const scheduledDate = new Date(this.workOrderData.scheduledDate);
        this.formData.scheduledDate = this.formatDateForPicker(scheduledDate);
        this.formData.scheduledTime = this.formatTimeFromDate(scheduledDate);
      }

      // Set assigned technician
      this.formData.technicianIds = 'technicianId' in this.workOrderData && this.workOrderData.technicianId
        ? [this.workOrderData.technicianId]
        : [];
    }
  }

  private async loadTechnicians(): Promise<void> {
    try {
      this.isLoadingTechnicians = true;
      const response = await this.technicianService.getTechniciansAsync([this.workOrderId!], this.cancelSubject);
      this.technicians = response.data ?? [];
      this.filteredTechnicians = [...this.technicians];

      this.isLoadingTechnicians = false
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.isLoadingTechnicians = false
      });
    }
  }

  private generateTimeOptions(): void {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        times.push(`${hourStr}:${minuteStr}`);
      }
    }

    this.timeOptions = times;
  }

  // Step navigation
  get canGoNext(): boolean {
    if (this.currentStep === 1) {
      const hasDate = !!this.formData.scheduledDate && this.formData.scheduledDate.trim() !== '';
      const hasTime = !!this.formData.scheduledTime && this.formData.scheduledTime.trim() !== '';
      return hasDate && hasTime;
    }
    return true;
  }

  get canSave(): boolean {
    return this.formData.technicianIds.length > 0;
  }

  get selectedTechnicians(): WorkOrderTechnician[] {
    return this.formData.technicianIds
      .map(technicianId => this.technicians.find(technician => technician.technicianId === technicianId))
      .filter((technician): technician is WorkOrderTechnician => !!technician);
  }

  nextStep(): void {
    if (this.currentStep === 1) {
      this.touched.scheduledDate = true;
      this.touched.scheduledTime = true;
    }

    if (this.currentStep < this.totalSteps && this.canGoNext) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  // Technician selection
  assignTechnician(technician: WorkOrderTechnician): void {
    if (this.isTechnicianSelected(technician.technicianId)) return;

    this.formData.technicianIds = [...this.formData.technicianIds, technician.technicianId];
  }

  unassignTechnician(technician: WorkOrderTechnician): void {
    this.formData.technicianIds = this.formData.technicianIds.filter(technicianId => technicianId !== technician.technicianId);
  }

  isTechnicianSelected(technicianId: number): boolean {
    return this.formData.technicianIds.includes(technicianId);
  }

  // Search functionality
  onSearchChange(): void {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredTechnicians = [...this.technicians];
    } else {
      this.filteredTechnicians = this.technicians.filter(t =>
        t.technicianName.toLowerCase().includes(query) ||
        (t.jobTitle && t.jobTitle.toLowerCase().includes(query))
      );
    }
  }

  // Date change handler
  onScheduledDateChange(value: string | undefined): void {
    this.formData.scheduledDate = value ?? '';
    this.touched.scheduledDate = true;
  }

  // Time dropdown methods
  toggleTimeDropdown(): void {
    if (this.timeDropdownOpen) {
      this.touched.scheduledTime = true;
    }
    this.timeDropdownOpen = !this.timeDropdownOpen;
  }

  selectTime(time: string): void {
    this.formData.scheduledTime = time;
    this.timeDropdownOpen = false;
    this.touched.scheduledTime = true;
  }

  closeTimeDropdown(): void {
    this.timeDropdownOpen = false;
  }

  // Save
  async save(): Promise<void> {
    if (!this.canSave) return;

    this.isLoading = true;

    try {
      // Parse date and time (Flatpickr format is Y/m/d = YYYY/M/DD)
      const dateParts = this.formData.scheduledDate.split('/');
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const day = parseInt(dateParts[2], 10);

      // Parse 24-hour time format (HH:MM)
      const timeParts = this.formData.scheduledTime.split(':');
      const hours = timeParts.length === 2 ? parseInt(timeParts[0], 10) : 0;
      const minutes = timeParts.length === 2 ? parseInt(timeParts[1], 10) : 0;

      const scheduledDate = new Date(year, month, day, hours, minutes);

      if (this.approveMode && this.onApprove) {
        await this.onApprove(scheduledDate, this.formData.technicianIds);
        this.isLoading = false;
        return;
      }

      const resolvedWorkOrderId = this.workOrderId
        ?? (this.workOrderData && 'id' in this.workOrderData ? this.workOrderData.id : null);
      if (!resolvedWorkOrderId) return;

      const request: ScheduleWorkOrderRequest = {
        workOrderId: resolvedWorkOrderId,
        scheduledDate,
        technicianIds: this.formData.technicianIds
      };

      await this.workOrdersService.scheduleWorkOrderAsync(request, this.cancelSubject);

      this.toastr.success(this.transloco.translate('workOrder.toasts.scheduled'));
      this.modalInstance.close(request);
      this.isLoading = false
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.isLoading = false
      });
    }
  }

  closeModal(): void {
    this.modalInstance.dismiss();
  }

  private formatDateForPicker(date: Date): string {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    // Return format matching Flatpickr's Y/m/d format (YYYY/M/DD)
    return `${year}/${month}/${day}`;
  }

  private formatTimeFromDate(date: Date): string {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
}
